import { useState, useEffect, useRef, useMemo } from "react";
import { AIBlockService } from "frontend/services/ai-blocks/ai-block-service";
import style from "./ai-generator-panel.module.css";
import { useAtomValue } from "jotai";
import { boardAtom } from "state-atoms";
import { documentIdAtom } from "state-atoms/stage-atoms";
import { generateAIBlock, searchTemplatesByEmbedding } from "frontend/api";
import { MondayBoard } from "shared/integrations/mapper";
import { AIGanttIcon, VisalizeBoardIcon } from "frontend/icons/icons";
import { AIFlowchartIcon, AIBrainstormIcon, AIProcessMapIcon, AITeamSyncIcon, AIOrgchartIcon } from "frontend/icons/ai";
import { boardDependencyMapBlock } from "frontend/services/ai-blocks/board-blocks/board-dependency-map-block";
import { boardGanttChartBlock } from "frontend/services/ai-blocks/board-blocks/board-gantt-chart-block";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { getDefaultColumnsMapping } from "utils/monday-utils";
import { MondayIntegrationType } from "shared/integrations/integration";
import { BoardContext } from "shared/ai-model/board-context";
import { CloseIcon } from "frontend/icons/close";
import { isMac } from "frontend/utils/keyboard-shortcuts";
import { AiLoader } from "./ai-loader/ai-loader";
import { useTemplateAutoPlacement } from "frontend/hooks/place-template-hooks";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { AIFeedback, useSetAiFeedBackContext } from "frontend/ui-components/ai-feedback/ai-feedback";

const promptTemplates = {
  "Gantt chart": { prompt: "Create a Gantt chart for ", icon: AIGanttIcon },
  Flowchart: { prompt: "Create a flowchart to map out ", icon: AIFlowchartIcon },
  "Org chart ": { prompt: "Create an org chart for ", icon: AIOrgchartIcon },
  Brainstorm: { prompt: "Create a brainstorm session for ", icon: AIBrainstormIcon },
  "Process map": { prompt: "Create a process map for ", icon: AIProcessMapIcon },
  "Team sync": { prompt: "Create a team sync template for ", icon: AITeamSyncIcon },
};

interface AIGeneratorPanelProps {
  onClose?: () => void;
}

export function AIGeneratorPanel({ onClose }: AIGeneratorPanelProps) {
  const documentId = useAtomValue(documentIdAtom).documentId;
  const board = useAtomValue(boardAtom);
  const { mondayIntegration, addMondayIntegration } = useBoardTasks(documentId);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const { setAIFeedbackContextByKey, clearContext } = useSetAiFeedBackContext("ai-generator");
  const autoPlaceTemplate = useTemplateAutoPlacement();

  const aiBlockService = AIBlockService.getInstance();

  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBoard, _] = useState<MondayBoard | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);

  const keyboardShortcutText = useMemo(() => {
    return isMac() ? "⌘↵" : "";
  }, []);

  useEffect(() => {
    // Register AI blocks on mount
    aiBlockService.registerBlock("board-dependency-map", boardDependencyMapBlock);
    aiBlockService.registerBlock("board-gantt-chart", boardGanttChartBlock);
  }, [aiBlockService]);

  useEffect(() => {
    tracking.trackAnalyticsEvent("ai_template_search", {
      category: consts.TRACKING_CATEGORY.AI_GENERATOR,
      opened_panel: true,
    });
  }, []);

  const setPromptAndClearFeedback = (value: string) => {
    setPrompt(value);
    clearContext();
  };

  const handlePromptTemplateClick = (selectedPrompt: string) => {
    setPromptAndClearFeedback(selectedPrompt);
    if (promptInputRef.current) {
      promptInputRef.current.focus();
    }
  };

  const generateBoardVisualization = async () => {
    if (!selectedBoard || !mondayIntegration) return;

    const templateSelection = await generateAIBlock("board-template-selector", prompt);

    // Create Monday.com integrations if needed
    const columns = getDefaultColumnsMapping(selectedBoard.columns, MondayIntegrationType.tasks);
    let boardIntegrationId: string | undefined;
    let subitemsBoardIntegrationId: string | undefined;

    if (aiBlockService.shouldCreateIntegration(templateSelection.templateType)) {
      boardIntegrationId = await addMondayIntegration(selectedBoard.id, mondayIntegration.id, columns);
    }
    if (selectedBoard.subitem_board) {
      subitemsBoardIntegrationId = await addMondayIntegration(
        selectedBoard.subitem_board.id,
        mondayIntegration.id,
        columns
      );
    }

    const boardContext: BoardContext = {
      integrationId: mondayIntegration.id,
      boardId: selectedBoard.id,
      boardName: selectedBoard.name,
      documentId: documentId,
      boardIntegrationId,
      subitemsBoardIntegrationId,
    };

    const { elements, metadata } = await aiBlockService.generate(templateSelection.templateType, {
      prompt,
      position: { x: 0, y: 0 },
      boardContext,
      onSuccess: (blockName, prompt) => {
        setTimeout(() => {
          setAIFeedbackContextByKey(prompt, blockName);
        }, 1500);
      },
    });

    const elementsObject = elements instanceof Map ? Object.fromEntries(elements) : elements;

    await autoPlaceTemplate(
      {
        id: `board-visualization-${Date.now()}`,
        name: `Board Visualization: ${selectedBoard.name}`,
        data: {
          elements: elementsObject,
          metadata: metadata || {},
        },
      },
      "ai-generator-panel"
    );

    setTimeout(() => {
      setAIFeedbackContextByKey(prompt, "board-visualization");
    }, 1500);
  };

  const generateFromTemplateSearch = async () => {
    if (!board) {
      throw new Error("Board not available");
    }
    tracking.trackAnalyticsEvent("ai_template_search", {
      category: consts.TRACKING_CATEGORY.AI_GENERATOR,
      prompt,
    });
    const searchResult = await searchTemplatesByEmbedding(prompt, 1);

    if (!searchResult.results || searchResult.results.length === 0) {
      tracking.trackAnalyticsEvent("ai_template_search_response", {
        category: consts.TRACKING_CATEGORY.AI_GENERATOR,
        prompt,
        template_id: "0",
        template_name: "no result",
      });
      throw new Error("No suitable template found for your request");
    }

    const selectedTemplate = searchResult.results[0];

    tracking.trackAnalyticsEvent("ai_template_search_response", {
      category: consts.TRACKING_CATEGORY.AI_GENERATOR,
      prompt,
      template_id: selectedTemplate.id,
      template_name: selectedTemplate.name,
    });

    await autoPlaceTemplate(
      {
        id: selectedTemplate.id.toString(),
        name: selectedTemplate.name,
        alias: selectedTemplate.alias,
      },
      "ai-generator-panel"
    );
    setTimeout(() => {
      setAIFeedbackContextByKey(prompt, "template-search");
    }, 1500);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    setError(null);

    try {
      if (selectedBoard && mondayIntegration) {
        await generateBoardVisualization();
      } else {
        await generateFromTemplateSearch();
      }
      setPrompt("");
    } catch (error) {
      console.error("Error generating template:", error);
      const message =
        error instanceof Error ? error.message : "Something went wrong. Try visualizing again, or refine your prompt.";
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Use a ref to always have access to the latest handleGenerate without dependencies
  const handleGenerateRef = useRef(handleGenerate);
  useEffect(() => {
    handleGenerateRef.current = handleGenerate;
  });

  useEffect(() => {
    // Handle cmd+enter keyboard shortcut
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        handleGenerateRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []); // Empty dependency array - the ref always has the latest function

  return (
    <div className={style.panelContainer}>
      <div className={style.panel} ref={panelRef}>
        <div className={style.header}>
          <div className={style.headerContent}>
            <VisalizeBoardIcon />
            <h3>Your canvas, built by AI</h3>
          </div>
          <div className={style.closeButton} onClick={onClose}>
            <CloseIcon />
          </div>
        </div>

        <div className={style.content}>
          {/* {mondayIntegration && (
          <div className={style.boardPickerSection}>
            <label className={style.boardPickerLabel} htmlFor="monday-board-picker">
              Select a board to visualize, either from the list or using the AI prompt.
            </label>
            <LiveIntegrationBoardPicker
              mondayIntegrationId={mondayIntegration.id}
              selectedBoardId={selectedBoard?.id}
              onSelectBoard={(board) => setSelectedBoard(board)}
              showClearButton={true}
              pickerStyle={DropdownPickerStyle.Light}
              styles={{ rounded: true }}
              width={256}
            />
          </div>
        )} */}

          <p className={style.boardPickerLabel}>Describe your use case to create a custom, editable template</p>
          <div className={style.promptInputContainer}>
            <textarea
              className={`${style.promptInput}`}
              placeholder="Let’s create your template..."
              value={prompt}
              onChange={(e) => setPromptAndClearFeedback(e.target.value)}
              disabled={isGenerating}
              rows={3}
              ref={promptInputRef}
            />

            {!prompt && (
              <div className={style.templateGrid}>
                {Object.entries(promptTemplates).map(([key, { prompt: templatePrompt, icon }]) => (
                  <div
                    key={key}
                    className={style.templateOption}
                    onClick={() => handlePromptTemplateClick(templatePrompt)}
                  >
                    {icon()}
                    {key}
                  </div>
                ))}
              </div>
            )}

            {isGenerating && (
              <div className={style.loaderWrapper}>
                <AiLoader />
              </div>
            )}
          </div>

          {error && <div className={style.error}>{error}</div>}

          <div className={style.blockButtons}>
            <button className={style.generateButton} onClick={handleGenerate} disabled={isGenerating || !prompt.trim()}>
              {isGenerating ? "Generating..." : `Visualize ${keyboardShortcutText}`}
            </button>
          </div>
        </div>
      </div>
      <AIFeedback contextKey="ai-generator" />
    </div>
  );
}
